import { useEffect, useState, type RefObject } from "react";

const BOTTOM_PAD = 8;

function parseBottomChrome(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--shell-bottom-chrome").trim();
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : 65;
}

/** Keeps a scroll region within the viewport below shell chrome. */
export function useTableViewportHeight(
  anchorRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  fixedMaxHeight?: number,
): number | undefined {
  const [maxHeight, setMaxHeight] = useState<number | undefined>(fixedMaxHeight);

  useEffect(() => {
    if (fixedMaxHeight != null) {
      setMaxHeight(fixedMaxHeight);
      return;
    }
    if (!enabled) {
      setMaxHeight(undefined);
      return;
    }

    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const next = Math.max(160, window.innerHeight - top - parseBottomChrome() - BOTTOM_PAD);
      setMaxHeight(next);
    };

    update();
    const ro = new ResizeObserver(update);
    if (anchorRef.current) ro.observe(anchorRef.current);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [anchorRef, enabled, fixedMaxHeight]);

  return maxHeight;
}

export function useInfiniteScrollSentinel(
  scrollRef: RefObject<HTMLElement | null>,
  sentinelRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  hasNextPage: boolean,
  isFetching: boolean | undefined,
  onLoadMore: (() => void) | undefined,
): void {
  useEffect(() => {
    if (!enabled || !hasNextPage || !onLoadMore) return;
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !isFetching) onLoadMore();
      },
      { root, rootMargin: "120px", threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [scrollRef, sentinelRef, enabled, hasNextPage, isFetching, onLoadMore]);
}
